'use client';

import { Suspense, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { Container, Section } from '@/components/primitives';
import { Header } from '@/components/header';
import { Icon } from '@/components/icons';
import { Footer } from '@/components/footer';
import { BookCard } from '@/components/book-card';
import { EmptyState } from '@/components/empty-state';
import { clientSearch } from '@/lib/client-api';
import type { Book } from '@/types/catalog';

// useSearchParams needs a Suspense boundary to satisfy Next's static prerender.
export default function SearchPage() {
  return (
    <Suspense fallback={null}>
      <SearchPageContent />
    </Suspense>
  );
}

function SearchPageContent() {
  const params = useSearchParams();
  const router = useRouter();
  const q = (params.get('q') ?? '').trim();

  const [term, setTerm] = useState(q);
  const [results, setResults] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setTerm(q); }, [q]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next = term.trim();
    if (next) router.replace(`/buscar?q=${encodeURIComponent(next)}`);
  };

  useEffect(() => {
    let active = true;
    if (!q) {
      setResults([]);
      return;
    }
    setLoading(true);
    clientSearch(q).then((r) => {
      if (!active) return;
      setResults(r);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [q]);

  return (
    <div className="page-fade">
      <Header current="search" />
      <Section pad="lg">
        <Container>
          <form onSubmit={onSubmit} style={{ position: 'relative', maxWidth: 560, marginBottom: 28 }}>
            <input
              className="input"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Buscar autor, título o tema"
              aria-label="Buscar en el catálogo"
              autoFocus={!q}
              style={{ width: '100%', padding: '12px 14px 12px 40px', fontSize: 15 }}
            />
            <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--stone)' }}>
              <Icon.search />
            </span>
          </form>
          <div className="eyebrow" style={{ marginBottom: 14 }}>Búsqueda</div>
          <h1 className="serif" style={{ margin: 0, fontSize: 52, lineHeight: 1.02, letterSpacing: '-0.02em', fontWeight: 600 }}>
            {q ? `“${q}”` : 'Comienza tu búsqueda'}
          </h1>
          <p style={{ marginTop: 16, color: 'var(--ink-soft)', fontSize: 16 }}>
            {q
              ? loading
                ? 'Buscando…'
                : `${results.length} resultados en el catálogo.`
              : 'Escribe un autor, título o tema en la barra de búsqueda.'}
          </p>
        </Container>
      </Section>

      <Section pad="md">
        <Container>
          {results.length > 0 ? (
            <div>
              {results.map((b) => (
                <BookCard key={b.id} book={b} variant="list" />
              ))}
            </div>
          ) : q && !loading ? (
            <EmptyState
              title="Sin resultados"
              body={`No encontramos títulos para “${q}”. Prueba con otro autor o tema.`}
            />
          ) : null}
        </Container>
      </Section>

      <Footer />
    </div>
  );
}
